import React from 'react';

export default function About() {
  const focusAreas = [
    { icon: 'bi-book-half', title: 'Education', text: 'Promoting and enriching the national educational ecosystem alongside native culture and traditions.' },
    { icon: 'bi-lightning-charge', title: 'Skill Development', text: 'Transforming lives via employment-focused skill training and direct placement initiatives.' },
    { icon: 'bi-grid-1x2', title: 'Clusters', text: 'Nurturing rural and localized community growth clusters for structural progress.' },
    { icon: 'bi-heart-pulse', title: 'Health', text: 'Driving health awareness campaigns and accessible medical support initiatives.' },
    { icon: 'bi-people', title: 'Citizen Interfacing Services', text: 'Bridging the gap between essential public utility services and everyday citizens.' }
  ];

  const sscAffiliations = [
    { id: 1, name: 'Construction Sector Skill Council of India (CSDCI)', icon: 'bi-building-gear' },
    { id: 2, name: 'Plumbing Sector Skill Council', icon: 'bi-wrench-adjustable' },
    { id: 3, name: 'Healthcare Sector Skill Council', icon: 'bi-heart-pulse-fill' },
    { id: 4, name: 'Apparel Sector Skill Council', icon: 'bi-scissors' }
  ];

  return (
    <section id="about" className="py-4">
      {/* Overview Block */}
      <div className="row align-items-center g-5 mb-5">
        <div className="col-lg-6">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="text-primary fw-bold text-uppercase tracking-wider small">Since 1996</span>
            <div className="bg-primary opacity-25" style={{ height: '2px', width: '50px' }}></div>
          </div>
          <h2 className="fw-extrabold display-5 mb-4 text-dark">
            Who We Are & <br />
            <span className="text-primary">What Drives Us</span>
          </h2>
          <p className="lead text-muted mb-4">
            Established on <strong>February 12, 1996</strong>, we invite each and every community to share their views, thoughts, and ideas to promote our country’s educational system, culture, and core traditions. 
          </p>
          <p className="text-secondary mb-4">
            Beyond education and culture, almost all services that help to enrich our country are fostered here. We firmly believe that without local participation, one cannot attain sustainable development—especially within underprivileged areas across India. 
          </p>
          
          {/* Enhanced Quote Card */}
          <div className="p-4 bg-primary bg-opacity-5 border-start border-primary border-4 rounded-end shadow-sm my-4">
            <i className="bi bi-quote fs-2 text-primary opacity-50 d-block mb-1"></i>
            <p className="fst-italic mb-0 text-dark fw-medium lh-base">
              "We understand the importance of community involvement, so we always consider the indigenous community across all execution phases—including thought leadership, localized implementation, and structural control mechanisms."
            </p>
          </div>
        </div>

        {/* Dynamic Focus Areas Grid */}
        <div className="col-lg-6" id="focus-areas">
          <div className="text-center text-lg-start mb-4">
            <h3 className="fw-bold mb-2">Our Strategic Focus Areas</h3>
            <p className="text-muted small">Comprehensive intervention approaches designed for long-term livelihood enhancement.</p>
          </div>
          <div className="row g-3">
            {focusAreas.map((area, idx) => (
              <div className="col-sm-6" key={idx}>
                <div className="card h-100 border-0 shadow-sm p-4 hover-card transition-all">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2.5 me-3 icon-box">
                      <i className={`bi ${area.icon} fs-4`}></i>
                    </div>
                    <h5 className="card-title mb-0 fw-bold text-dark-emphasis fs-6">{area.title}</h5>
                  </div>
                  <p className="card-text text-muted small mb-0 lh-sm">{area.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strategic Mission / Vision / Oath Row */}
      <div className="row g-4 mb-5">
        <div className="col-md-6">
          <div className="card border-0 bg-dark text-white p-4 h-100 rounded-3 shadow-sm">
            <div className="card-body">
              <h4 className="fw-bold text-warning mb-3"><i className="bi bi-flag-fill me-2"></i> Our Core Mandate</h4>
              <p className="card-text text-white-50 mb-0">
                Nation building is the sacred oath taken by all members of the SOCIETY FOR THE "WATER" until total structural development and real ground level equity are systematically realized. We assure our absolute best efforts for the ultimate upliftment of down-trodden societies across the country.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 bg-primary text-white p-4 h-100 rounded-3 shadow-sm">
            <div className="card-body">
              <h4 className="fw-bold text-white mb-3"><i className="bi bi-graph-up me-2"></i> Comprehensive Approach</h4>
              <p className="card-text text-white-50 mb-0">
                SFW stands tall as one of the fastest growing skill architectures in the nation. We focus primarily on transforming structural ecosystems through a thoroughly planned, holistic approach to guarantee sustainable employment, wage equity, and livelihood optimization.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New Section: SSC Affiliations Banner */}
      <div className="bg-white border rounded-3 p-4 p-md-5 shadow-sm">
        <div className="text-center mb-4 max-w-md mx-auto">
          <span className="badge bg-success-subtle text-success px-3 py-1.5 rounded-pill fw-bold uppercase tracking-wider mb-2">Accredited Compliance</span>
          <h4 className="fw-bold text-dark">Official Sector Skill Council (SSC) Affiliations</h4>
          <p className="text-muted small mb-0">We maintain verified industry alignment to provide accredited standard training across major national economic domains.</p>
        </div>
        
        <div className="row g-3">
          {sscAffiliations.map((ssc) => (
            <div className="col-xl-3 col-sm-6" key={ssc.id}>
              <div className="d-flex align-items-center p-3 bg-light rounded border border-light h-100 shadow-xsm hover-sync">
                <div className="bg-white text-primary shadow-sm rounded p-2.5 d-flex align-items-center justify-content-center me-3" style={{ width: '45px', height: '45px' }}>
                  <i className={`bi ${ssc.icon} fs-5`}></i>
                </div>
                <div className="lh-sm">
                  <div className="text-muted font-monospace extra-small">COUNCIL #0{ssc.id}</div>
                  <span className="fw-bold text-dark-emphasis small d-block mt-0.5">{ssc.name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Embedded Component Scope Styles */}
      <style>{`
        .transition-all { transition: all 0.25s ease-in-out; }
        .hover-card:hover {
          transform: translateY(-5px);
          background-color: #ffffff !important;
          box-shadow: 0 .5rem 1.5rem rgba(13, 110, 253, 0.08) !important;
        }
        .hover-card:hover .icon-box {
          background-color: #0d6efd !important;
          color: #ffffff !important;
          transition: all 0.25s ease-in-out;
        }
        .hover-sync:hover {
          border-color: #cbd5e1 !important;
          background-color: #f8fafc !important;
          transition: all 0.2s ease;
        }
        .extra-small { font-size: 0.65rem; letter-spacing: 0.5px; }
        .shadow-xsm { box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
      `}</style>
    </section>
  );
}